import React from "react";
import { Box, ThemeProvider, createTheme } from "@mui/material";
import { CategoryReference, urgencyStatus } from "../../myRfqs/models/models";
import { RfqsQuery } from "../models/models";
import { classNames, enumToArray } from "../../../shared/models/models";
import TableToolbar from "../../../shared/components/tables/TableToolbar";
import TableToolbarSearch from "../../../shared/components/tables/TableToolbarSearch";
import TableToolbarSelect from "../../../shared/components/tables/TableToolbarSelect";
import TableToolbarCheckbox from "../../../shared/components/tables/TableToolbarCheckbox";
import { ProductConditionCode, UrgencyCode, productCondition } from "../../../common/models/CommonModels";
import colors from "../../../shared/themes/colors";
import useTranslation from "../../../shared/hooks/useTranslation";

type RfqFeedTableToolbarProps = {
  categories?: CategoryReference[];
  query: RfqsQuery;
  className?: string;
  onChange: (query: RfqsQuery) => void;
};

const RfqFeedTableToolbar = ({ categories, query, className, onChange }: RfqFeedTableToolbarProps) => {
  const { translate } = useTranslation("common");
  const urgencyOptions = enumToArray<UrgencyCode>(UrgencyCode).map((x) => ({ id: x, name: urgencyStatus(x) }));

  const conditionOptions = [ProductConditionCode.new, ProductConditionCode.refit, ProductConditionCode.used].map(
    (x) => ({ id: x, name: translate(`productCondition.${productCondition(x)}`), checked: !!query?.conditionCodes?.some((y) => y === x) })
  );

  const handleQueryChange = (query: RfqsQuery): void => {
    if (onChange) {
      onChange(query);
    }
  };

  const handleSearch = (value?: string) => {
    handleQueryChange({ ...query, search: value });
  };

  const handleUrgencyChange = (urgencyCode?: UrgencyCode) => {
    handleQueryChange({ ...query, urgencyCode: urgencyCode });
  };

  const handleCategoryChange = (categoryId?: string) => {
    handleQueryChange({ ...query, categoryId: categoryId });
  };

  const handleConditionChange = (conditionCode: ProductConditionCode, isEnabled: boolean) => {
    const conditionOption = conditionOptions.find((x) => x.id === conditionCode);
    if (conditionOption) {
      conditionOption.checked = isEnabled;
    }

    const conditionCodes = conditionOptions.filter((x) => x.checked).map((x) => x.id);

    handleQueryChange({ ...query, conditionCodes: conditionCodes });
  };

  const theme = createTheme({});

  return (
    <ThemeProvider theme={theme}>
      <TableToolbar className={classNames("flex items-center justify-between", className)}>
        <Box className="flex gap-4">
          <TableToolbarSearch
            placeholder="Search by number/alternative number"
            value={query.search}
            onChange={handleSearch}
          />

          <TableToolbarSelect
            label="Urgency"
            items={urgencyOptions}
            value={query.urgencyCode}
            onChange={handleUrgencyChange}
          />

          <TableToolbarSelect
            label="Category"
            items={categories}
            value={query.categoryId}
            onChange={handleCategoryChange}
          />
        </Box>

        <Box className="flex items-center">
          <Box className="mr-3 text-14-medium" color={colors.secondary.gray80}>
            CONDITION:
          </Box>
          <Box className="flex gap-1">
            {conditionOptions.map((x) => (
              <TableToolbarCheckbox
                key={x.id}
                label={x.name}
                checked={x.checked}
                onChange={() => handleConditionChange(x.id, !x.checked)}
              />
            ))}
          </Box>
        </Box>
      </TableToolbar>
    </ThemeProvider>
  );
};
export default RfqFeedTableToolbar;
